import React from "react";

export const cx = (...parts) => parts.filter(Boolean).join(" ");

const BUTTON_VARIANTS = {
  primary: "bg-signal text-ink border-0 hover:brightness-105",
  outline: "bg-white border-[1.5px] border-line text-stone-600 hover:border-ink",
  dark: "bg-ink text-white border-0 hover:bg-ink-soft",
  danger: "bg-transparent border-[1.5px] border-[#c98b8b] text-red-700 hover:bg-red-50"
};

const BUTTON_SIZES = {
  sm: "h-9 px-3 text-[10px]",
  md: "h-12 px-4 text-xs",
  lg: "h-[52px] px-5 text-[13px]"
};

export function Button({ variant = "primary", size = "md", className, children, ...rest }) {
  return (
    <button
      className={cx(
        "mono font-semibold uppercase tracking-[0.14em] inline-flex items-center justify-center gap-2 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed",
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        className
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

export function LinkButton({ tone = "danger", className, children, ...rest }) {
  return (
    <button
      className={cx(
        "mono text-[10px] uppercase tracking-[0.1em] bg-transparent border-0 underline cursor-pointer p-0",
        tone === "danger" ? "text-red-700" : "text-signal-deep",
        className
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

export const Field = React.forwardRef(function Field({ as = "input", dark = false, className, ...rest }, ref) {
  const Tag = as;
  return (
    <Tag
      ref={ref}
      className={cx(
        "w-full box-border text-[15px] focus:outline-none",
        dark
          ? "bg-ink border border-ink-soft text-white placeholder-[#6b6e74] px-4 focus:border-signal"
          : "bg-white border border-[#c4c0b9] text-ink placeholder-muted px-3 focus:border-ink",
        as === "textarea" ? "py-2 resize-none" : dark ? "h-[50px]" : "h-11",
        className
      )}
      {...rest}
    />
  );
});

export function Chip({ active = false, className, children, ...rest }) {
  return (
    <button
      type="button"
      className={cx(
        "mono text-[10px] font-semibold uppercase tracking-[0.12em] px-3 py-2 border cursor-pointer flex-shrink-0",
        active ? "bg-ink text-white border-ink" : "bg-white text-stone-600 border-line",
        className
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

export function MonoLabel({ className, children, ...rest }) {
  return (
    <span
      className={cx("mono text-[10px] uppercase tracking-[0.14em] text-muted", className)}
      {...rest}
    >
      {children}
    </span>
  );
}

export function SectionHeader({ children, right, muted = false, className }) {
  return (
    <div className={cx("flex items-center gap-2.5", className)}>
      <span
        className={cx(
          "mono text-[10px] font-semibold uppercase tracking-[0.18em]",
          muted ? "text-muted" : "text-ink"
        )}
      >
        {children}
      </span>
      <span className="flex-1 h-px bg-line" />
      {right}
    </div>
  );
}

export function ProgressBar({ value = 0, max = 1, full = false, className }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return (
    <div className={cx("h-[5px] bg-[#e8eaec]", className)}>
      <div
        className={cx("h-full", full ? "bg-ink" : "bg-signal")}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

const AVATAR_TONES = {
  signal: "bg-signal text-ink",
  dark: "bg-ink text-white",
  light: "bg-surface text-ink",
  tint: "bg-signal-tint text-signal-bright"
};

export function Avatar({ initials, size = 36, tone = "light", className }) {
  return (
    <div
      className={cx(
        "rounded-full mono font-semibold flex items-center justify-center flex-shrink-0",
        AVATAR_TONES[tone],
        className
      )}
      style={{ width: size, height: size, fontSize: Math.round(size / 3) }}
    >
      {initials}
    </div>
  );
}

export function StatBlock({ label, value, sub, accent = false, className }) {
  return (
    <div className={cx("bg-white px-4 py-3.5", className)}>
      <MonoLabel className="text-[9px]">{label}</MonoLabel>
      <div className={cx("display text-[42px] leading-[0.82] mt-1", accent && "text-pass-deep")}>
        {value}
      </div>
      {sub && <div className="mono text-[10px] text-muted mt-1">{sub}</div>}
    </div>
  );
}

export function BottomNav({ items, active, onSelect }) {
  return (
    <nav className="fixed bottom-0 inset-x-0 z-20 bg-ink">
      <div className="max-w-screen-sm lg:max-w-4xl mx-auto flex px-2 pt-2.5 pb-[calc(0.8rem+env(safe-area-inset-bottom))]">
        {items.map((item) => {
          const ItemIcon = item.icon;
          const on = item.id === active;
          return (
            <button
              key={item.id}
              onClick={() => onSelect(item.id)}
              className={cx(
                "flex-1 flex flex-col items-center gap-1 bg-transparent border-0 cursor-pointer",
                on ? "text-signal-bright" : "text-[#6b6e74]"
              )}
            >
              <span className="relative">
                <ItemIcon size={21} className={on ? "anim-nav-pop" : undefined} />
                {item.badge > 0 && (
                  <span className="absolute -top-1.5 -right-2 min-w-[15px] h-[15px] px-1 bg-signal text-ink mono text-[9px] font-semibold flex items-center justify-center rounded-full">
                    {item.badge > 99 ? "99+" : item.badge}
                  </span>
                )}
              </span>
              <span className="mono text-[8px] uppercase tracking-[0.12em]">{item.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

export const initialsOf = (name = "") =>
  name
    .replace(/[^a-zA-Z ]/g, "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("") || "?";
